// Mapear os menus para identificação por categoria
const MENU_CATEGORIES = {
  0: "Dashboard",
  1: "Pedidos",
  2: "Estoque",
  3: "Cadastros", 
  4: "Produtos",
  5: "Etiquetas",
  6: "Configurações"
};

const SUBMENU_CATEGORIES = {
  1: {
    0: "Pedidos/Analise-margem"
  },
  2: {
    0: "Estoque/Ordem-de-compra",
    1: "Estoque/Analise-Estoque"
  },
  3: {
    0: "Cadastros/Skus"
  },
  4: {
    0: "Produtos/Catálogo",
    1: "Produtos/Métricas", 
    2: "Produtos/Campanhas"
  },
  5: {
    0: "Etiquetas/Por-NF-e",
    1: "Etiquetas/Por-Pedido"
  },
  6: {
    0: "Configurações/Plano-Atual",
    1: "Configurações/Integrações",
    2: "Configurações/Impostos"
  }
};

// Parser para categorias dos planos
const parseCategoryName = (category) => {
  // Remove espaços e converte para formato de comparação
  return category.replace(/-/g, ' ').toLowerCase();
};

const parseAllowedCategories = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }
  
  // Se tem "ALL", libera tudo
  if (categories.includes('ALL')) {
    return ['ALL'];
  }
  
  return categories.map(category => {
    // Parse do formato Categoria/Sub-categoria
    if (category.includes('/')) {
      const [parent, child] = category.split('/');
      return {
        parent: parseCategoryName(parent),
        child: parseCategoryName(child),
        fullPath: category
      };
    }
    
    return {
      parent: parseCategoryName(category),
      child: null,
      fullPath: category
    };
  });
};

const isValidPlan = (marketplacePlans) => {
  if (!marketplacePlans || marketplacePlans.length === 0) {
    return false;
  }
  
  const activePlan = marketplacePlans.find(plan => plan.status === 1);
  if (!activePlan) {
    return false;
  }
  
  const now = new Date();
  const expirationDate = new Date(activePlan.expire_in);
  
  if (expirationDate < now) {
    return false;
  }
  
  return true;
};

export const getMenuAccessInfo = (parentIndex, childIndex = null, marketplacePlans = [], userData = null) => {
  // Se não há plano válido, só libera "Configurações/Plano-Atual"
  if (!isValidPlan(marketplacePlans)) {
    const menuCategory = MENU_CATEGORIES[parentIndex];
    if (menuCategory !== 'Configurações') {
      return { hasAccess: false, reason: 'organization' };
    }
    
    if (childIndex !== null) {
      const submenuCategory = SUBMENU_CATEGORIES[parentIndex]?.[childIndex];
      return { 
        hasAccess: submenuCategory === 'Configurações/Plano-Atual', 
        reason: submenuCategory === 'Configurações/Plano-Atual' ? null : 'organization' 
      };
    }
    
    return { hasAccess: true, reason: null };
  }
  
  // Pega o plano ativo (status: 1)
  const activePlan = marketplacePlans.find(plan => plan.status === 1);
  const planAllowedCategories = parseAllowedCategories(activePlan.categories);
  
  // Verifica available_categories do usuário
  let userAllowedCategories = [];
  if (userData && userData.available_categories) {
    userAllowedCategories = parseAllowedCategories(userData.available_categories);
  }
  
  // Verifica se o plano tem acesso (PRIORIDADE)
  const planHasAccess = checkCategoryAccess(parentIndex, childIndex, planAllowedCategories);
  
  // Se o plano não tem acesso, bloqueia independente do usuário
  if (!planHasAccess) {
    return { hasAccess: false, reason: 'organization' };
  }
  
  // Verifica se o usuário tem acesso (somente se plano permitir)
  const userHasAccess = userAllowedCategories.length > 0 ? 
    checkCategoryAccess(parentIndex, childIndex, userAllowedCategories) : 
    true; // Se usuário não tem categorias específicas, usa permissão do plano
  
  // Determina a razão do bloqueio
  let reason = null;
  if (!userHasAccess) {
    // Se chegou aqui, plano permite mas usuário não tem acesso
    reason = 'user';
  }
  
  return { hasAccess: userHasAccess, reason };
};

// Função auxiliar para verificar acesso por categoria
const checkCategoryAccess = (parentIndex, childIndex, allowedCategories) => {
  // Se tem "ALL", libera tudo
  if (allowedCategories.includes('ALL')) {
    return true;
  }
  
  // Identifica a categoria do menu atual
  const menuCategory = MENU_CATEGORIES[parentIndex];
  if (!menuCategory) {
    return false;
  }
  
  // Se é submenu, verifica categoria/subcategoria
  if (childIndex !== null) {
    const submenuCategory = SUBMENU_CATEGORIES[parentIndex]?.[childIndex];
    if (submenuCategory) {
      // Verifica se a categoria/subcategoria específica está liberada
      const specificAccess = allowedCategories.some(allowed => {
        if (allowed === 'ALL') return true;
        
        const [parentCat, childCat] = submenuCategory.split('/');
        return allowed.parent === parseCategoryName(parentCat) && 
               allowed.child === parseCategoryName(childCat);
      });
      
      // Se não tem acesso específico, verifica se a categoria pai está liberada
      if (!specificAccess) {
        const [parentCat] = submenuCategory.split('/');
        return allowedCategories.some(allowed => {
          if (allowed === 'ALL') return true;
          // Se a categoria pai está liberada E não tem subcategoria específica definida, libera
          return allowed.parent === parseCategoryName(parentCat) && allowed.child === null;
        });
      }
      
      return specificAccess;
    }
  }
  
  // Verifica se a categoria pai está liberada
  return allowedCategories.some(allowed => {
    if (allowed === 'ALL') return true;
    return allowed.parent === parseCategoryName(menuCategory);
  });
};

export const hasMenuAccess = (parentIndex, childIndex = null, marketplacePlans = [], userData = null) => {
  return getMenuAccessInfo(parentIndex, childIndex, marketplacePlans, userData).hasAccess;
};

export const processMenusWithAccess = (menus, marketplacePlans = [], userData = null) => {
  return menus.map((menu, menuIndex) => {
    const processedMenu = { ...menu };
    
    processedMenu.hasAccess = hasMenuAccess(menuIndex, null, marketplacePlans, userData);
    
    if (menu.children && menu.children.length > 0) {
      processedMenu.children = menu.children.map((child, childIndex) => ({
        ...child,
        hasAccess: hasMenuAccess(menuIndex, childIndex, marketplacePlans, userData)
      }));
    }
    
    return processedMenu;
  });
};

export const hasFeatureAccess = (feature, marketplacePlans = [], userData = null) => {
  // Se não há planos, bloqueia
  if (!marketplacePlans || marketplacePlans.length === 0) {
    return false;
  }
  
  const activePlan = marketplacePlans.find(plan => plan.status === 1);
  if (!activePlan) {
    return false;
  }
  
  const planAllowedCategories = parseAllowedCategories(activePlan.categories);
  
  // Verifica available_categories do usuário
  let userAllowedCategories = [];
  if (userData && userData.available_categories) {
    userAllowedCategories = parseAllowedCategories(userData.available_categories);
  }
  
  // Verifica se o plano tem acesso (PRIORIDADE)
  const planHasFeatureAccess = checkFeatureAccess(feature, planAllowedCategories);
  
  // Se o plano não tem acesso, bloqueia independente do usuário
  if (!planHasFeatureAccess) {
    return false;
  }
  
  // Se usuário tem categorias específicas, verifica suas permissões
  if (userAllowedCategories.length > 0) {
    return checkFeatureAccess(feature, userAllowedCategories);
  }
  
  // Se usuário não tem categorias específicas, usa permissão do plano
  return true;
};

// Função auxiliar para verificar acesso a features
const checkFeatureAccess = (feature, allowedCategories) => {
  // Se tem "ALL", libera tudo
  if (allowedCategories.includes('ALL')) {
    return true;
  }
  
  // Mapear features para categorias
  const featureMap = {
    "purchase-orders": "estoque/ordem-de-compra",
    "stock-analysis": "estoque/analise-estoque"
  };
  
  const featureCategory = featureMap[feature];
  if (!featureCategory) {
    return false;
  }
  
  const [parent, child] = featureCategory.split('/');
  return allowedCategories.some(allowed => {
    if (allowed === 'ALL') return true;
    return allowed.parent === parseCategoryName(parent) && 
           allowed.child === parseCategoryName(child);
  });
};

export const getFirstAccessibleMenu = (menus, marketplacePlans = [], userData = null) => {
  // Se não há plano válido, redireciona para Configurações > Plano Atual
  if (!isValidPlan(marketplacePlans)) {
    return { menu: 6, submenu: 0 }; // Configurações > Plano Atual
  }

  for (let parentIndex = 0; parentIndex < menus.length; parentIndex++) {
    const menu = menus[parentIndex];
    
    if (menu.children && menu.children.length > 0) {
      for (let childIndex = 0; childIndex < menu.children.length; childIndex++) {
        const hasChildAccess = hasMenuAccess(parentIndex, childIndex, marketplacePlans, userData);
        if (hasChildAccess) {
          return { menu: parentIndex, submenu: childIndex };
        }
      }
    } else {
      const hasParentAccess = hasMenuAccess(parentIndex, null, marketplacePlans, userData);
      if (hasParentAccess) {
        return { menu: parentIndex, submenu: null };
      }
    }
  }
  
  return { menu: 6, submenu: 0 }; // Fallback para Configurações > Plano Atual
};

export { MENU_CATEGORIES, SUBMENU_CATEGORIES, parseCategoryName, parseAllowedCategories, isValidPlan };